'use strict';

const EventEmitter = require('events').EventEmitter,
    {t} = require('services/i18nextWrapper'),
    Constants = require('constants/AppConstants'),
    ContentUtils = require('utils/ContentUtils'),
    DateUtils = require('utils/DateUtils'),
    OrderUtils = require('utils/OrderUtils'),
    NumberUtils = require('utils/NumberUtils'),
    Notification = require('components/Notification/Notification'),
    DashboardLayoutStore = require('./DashboardLayoutStore'),
    DashboardStore = require('stores/DashboardStore'),
    BrewhouseStore = require('stores/BrewhouseStore'),
    OrderStore = require('stores/OrderStore'),
    DashboardServiceCache = require('services/DashboardServiceCache'),
    ChronologycStore = require('stores/ChronologycStore'),
    InventoryStore = require('stores/InventoryStore'),
    DashboardModalEl = require('components/DashboardModal/DashboardModalEl'),
    ContextMenuEl = require('components/Commons/ContextMenuCustom/ContextMenuEl'),
    ActionTypes = Constants.ActionTypes,
    _ = require('lodash'),
    assign = require('object-assign'),
    CHANGE_EVENT = 'change',
    SchedulerAppDispatcher = require('dispatcher/SchedulerAppDispatcher');

/**
 * Add state outside of store object because make issues Flux action dispatch callbacks.
 */
let context = {
    _lastToggleName: '',
    _currentLayout: [],
    _currentOrder: null,
    _batchNumber: null,
    _volumeOrder: null,
    _pristineVolume: null,
    _isBHAllowed: null,
    _isFVAllowed: null,
    _isBTAllowed: null,
    _lastBatchNumber: null,
    _lastManualVolume: null,
    _styleBeerSelected: null,
    _fermenterTankSelected: null,
    _briteTankTankSelected: null,
    _lastUpdatedTasks: []
};

const DashboardModalStore = assign({}, EventEmitter.prototype, {

    emitChange() {
        this.emit(CHANGE_EVENT);
    },

    setLastManualBatchNumber(batchNumber) {
        context._lastBatchNumber = batchNumber;
    },

    getLastManualBatchNumber() {
        return context._lastBatchNumber;
    },

    setStyleBeerSelected(styleBeerSelected) {
        context._styleBeerSelected = styleBeerSelected;
    },

    getStyleBeerSelected() {
        return context._styleBeerSelected;
    },

    setFermenterTankSelected(fermenterTankSelected) {
        context._fermenterTankSelected = fermenterTankSelected;
    },

    getFermenterTankSelected() {
        return context._fermenterTankSelected;
    },

    setBriteTankSelected(briteTankSelected) {
        context._briteTankTankSelected = briteTankSelected;
    },

    getBriteTankSelected() {
        return context._briteTankTankSelected;
    },

    setLastManualVolume(volume) {
        return context._lastManualVolume = volume;
    },

    getLastManualVolume() {
        return context._lastManualVolume;
    },

    setLastManualTask(task) {
        // find and replace if existing task
        if (!context._lastUpdatedTasks) {
            context._lastUpdatedTasks = [];
        }

        if (task) {
            // remove possible duplicated task
            context._lastUpdatedTasks = _.filter(context._lastUpdatedTasks, oldTask =>
                !(_.get(oldTask, 'date') === _.get(task, 'date') &&
                    _.get(oldTask, 'sectionType') === _.get(task, 'sectionType'))
            );
        }

        context._lastUpdatedTasks.push(task);
    },

    getLastManualTask() {
        return context._lastUpdatedTasks;
    },

    updateLayoutAndCalculateSectionVolumeLabels(order) {
        const batchNumber = _.get(order, 'batchNumber');
        const oldBatchNumber = _.get(order, 'oldBatchNumber');

        // use the last batch number over original batch number order
        // because if exist last batch number means the user prefer user them over the original
        const uniqueBatchNumbers = _.uniq([this.getLastManualBatchNumber(), batchNumber, oldBatchNumber]);
        const batchNumbersCleaner = _.filter(uniqueBatchNumbers, batchNumber => batchNumber);
        const modalLayout = getMinimalLayoutFromBatchNumber(batchNumbersCleaner, order);
        this.setCurrentLayout(modalLayout);

        // draw section volumes in modal
        DashboardModalEl.updateVolumeLabels(order);
        DashboardModalEl.updateBatchCostLabel(order);
    },

    updateBatchNumberOfLayout(orderId, oldBatchNumber, newBatchNumber) {
        this.addPositionsToCurrentLayoutFromBatchNumber(newBatchNumber);

        // update layout
        const isNonEmptyLayout = _.get(context._currentLayout, 'length', 0) > 0;
        const isDifferentOldBatchThanNewBatchNumber = oldBatchNumber !== newBatchNumber;

        if (isNonEmptyLayout && isDifferentOldBatchThanNewBatchNumber) {
            // enter here means that we change the batch number of current order, so we need to update the position of given
            // order so, we need to update currentLayout in order to update each batch number of each positions
            context._currentLayout = _.map(context._currentLayout, position => {
                if (ContentUtils.cleanId(position.i) === orderId) {
                    position.batchNumber = newBatchNumber;
                }
                return position;
            });
        }
    },

    addPositionsToCurrentLayoutFromBatchNumber(newBatchNumber) {
        // add the new position of the batch number
        const newPositionsOfNewBatchNumber = _.filter(DashboardLayoutStore.getLayout(), position => position.batchNumber === newBatchNumber);
        context._currentLayout = _.concat(context._currentLayout, newPositionsOfNewBatchNumber);

        // to be sure to not duplicate positions
        context._currentLayout = ContentUtils.removeDuplicateByAttribute(context._currentLayout, ['x', 'y', 'i', 'h']);
    },


    updateSectionLabels(batchVolumeInfo, order) {
        // save total batch volume label and underline text too
        const tasks = order.taskWithProductUpdates ? (Array.isArray(order.taskWithProductUpdates) ? order.taskWithProductUpdates : [order.taskWithProductUpdates]) : null;
        if (tasks) {
            batchVolumeInfo.productVolume = InventoryStore.calculateTotalVolumeFromTask(tasks, order);
            batchVolumeInfo.productVolumeLeft = NumberUtils.subtract(batchVolumeInfo.totalBatchVolume, batchVolumeInfo.productVolume);
        } else {
            batchVolumeInfo.productVolume = '0';
            batchVolumeInfo.productVolumeLeft = batchVolumeInfo.totalBatchVolume;
        }
        batchVolumeInfo.totalBatchVolumeMessageError = BrewhouseStore.getLabelVolumeBySections(batchVolumeInfo.fermentingVolume, batchVolumeInfo.packagingVolume);
        batchVolumeInfo.totalBatchVolumeLeftMessage = BrewhouseStore.formatBatchVolumeLeft(batchVolumeInfo.totalBatchVolume, batchVolumeInfo.productVolumeLeft);
        batchVolumeInfo.order = order;

        return batchVolumeInfo;
    },

    _updateVolumeInfoWithCurrentNewVolumeOrder(batchVolumeInfo, order) {
        const isSingle = _.get(order, 'isSingle');
        const newOrderVolume = _.get(order, 'volume');
        if (isSingle) {
            if (_.get(order, 'tankSelected.type') === 'FV') { // we can use `tankSelected` because is just an NEW order.
                batchVolumeInfo.fermentingVolume = NumberUtils.sum(batchVolumeInfo.fermentingVolume, newOrderVolume);
            } else {
                batchVolumeInfo.packagingVolume = NumberUtils.sum(batchVolumeInfo.packagingVolume, newOrderVolume);
            }
        } else {
            batchVolumeInfo.fermentingVolume = NumberUtils.sum(batchVolumeInfo.fermentingVolume, newOrderVolume);
            batchVolumeInfo.packagingVolume = NumberUtils.sum(batchVolumeInfo.packagingVolume, newOrderVolume);
        }
        batchVolumeInfo.totalBatchVolumeMessageError = BrewhouseStore.getLabelVolumeBySections(batchVolumeInfo.fermentingVolume, batchVolumeInfo.packagingVolume);

        return batchVolumeInfo;
    },

    setCurrentLayout(layout) {
        context._currentLayout = layout;
    },

    getCurrentLayout() {
        return context._currentLayout;
    },

    cleanStore() {
        context._currentLayout = null;
        context._lastBatchNumber = null;
        context._currentOrder = null;
        context._pristineVolume = null;
        context._volumeOrder = null;
        context._lastManualVolume = null;
        context._lastUpdatedTasks = [];
        context._lastToggleName = '';
        context._batchNumber = null;
        context._styleBeerSelected = null;
        context._fermenterTankSelected = null;
        context._briteTankTankSelected = null;
        context._isFVAllowed = true;
        context._isBTAllowed = true;
        context._isBHAllowed = true;
        context._isOpen = false;
    },

    getInitialState() {
        return Object.assign({
            isOpen: false,
            isNewModal: false,
            isBHAllowed: true,
            isFVAllowed: true,
            isBTAllowed: true,
            currencyInfo: NumberUtils.getCurrentCurrency()
        }, this.getInitialStateWithoutToggles());
    },

    getInitialStateWithoutToggles() {
        return {
            isOpen: false,
            isNewModal: false,
            shouldNeedValidate: true,
            currentTabSelected: 0,
            shouldNotUpdateChildren: false,
            leftVolumeHighlighted: false
        };
    },

    setCurrentOrder(order) {
        context._currentOrder = order;
    },

    getBatchNumber() {
        return context._batchNumber;
    },

    setBatchNumber(batchNumber) {
        context._batchNumber = batchNumber;
    },

    getCurrentOrder() {
        return context._currentOrder;
    },

    addChangeListener(call) {
        this.on(CHANGE_EVENT, call);
    },

    removeChangeListener(call) {
        this.removeListener(CHANGE_EVENT, call);
    },

    getLastToggleConditional() {
        return `is${context._lastToggleName}Allowed`;
    },

    setLastToggleConditional(name) {
        context._lastToggleName = name;
    },

    isBHAllowed() {
        return context._isBHAllowed;
    },

    isFVAllowed() {
        return context._isFVAllowed;
    },

    isBTAllowed() {
        return context._isBTAllowed;
    },

    setVolume(volumeOrder) {
        context._volumeOrder = volumeOrder;
    },

    getVolume() {
        return context._volumeOrder;
    },

    setToggles(isBHAllowed, isFVAllowed, isBTAllowed) {
        context._isBHAllowed = isBHAllowed;
        context._isFVAllowed = isFVAllowed;
        context._isBTAllowed = isBTAllowed;
    },

    getPristineVolume() {
        return context._pristineVolume;
    },

    setPristineVolume(volume) {
        context._pristineVolume = volume;
    },

    prepareOrderFromAction() {
        prepareOrderFromAction.apply(this, arguments);
    }

});

/**
 * Given a batch number generate a minimal dashboard layout orders needed by work an efficient way
 * in the dashboard modal which given particular batch number. (small part of dashboard)
 * @param {Object | Array} batchNumbers
 * @param {Object} batchNumbers (Optional)
 * @return {Array}
 */
function getMinimalLayoutFromBatchNumber(batchNumbers, order) {
    let ordersFromRange,
        batchOrders = [];

    // take all order with the same batch number (batch orders)
    if (Array.isArray(batchNumbers)) {
        for (const batchNumber of batchNumbers) {
            batchOrders = _.union(OrderStore.getOrderByBatchNumber(batchNumber), batchOrders);
        }
    } else {
        batchOrders = OrderStore.getOrderByBatchNumber(batchNumbers);
    }
    // put the current order from the list of batch orders
    batchOrders = _replaceOlderOrderToNewOrder(batchOrders, order);

    // update layout if needed update the position of batch number
    // against the batch number each order from the batch
    _syncOrderBatchNumberWithModalLayout(batchOrders);

    // get the older and latest date from the current batch order
    const earlyAndLaterOrderDate = _retrieveEarlyAndLaterOrder(batchOrders);

    // retrieve all batchOrders which are in the range of the batch order date
    ordersFromRange = OrderUtils.getOrderByRange(earlyAndLaterOrderDate.earlyDate, earlyAndLaterOrderDate.laterDate);

    // convert the orders to positions from the partial minimal layout generated
    let layout = _.flatten(_.map(ordersFromRange, order => OrderUtils.convertOrderToPositions(order, ChronologycStore.getCurrentFrom(), null, true)));

    // hydrate position with extra order information
    if (order) {
        layout = _updateLayoutWithOrder(layout, order);
    }

    return layout;

}

function _replaceOlderOrderToNewOrder(orders, newOrder) {
    const isChangeOrder = newOrder && (ContentUtils.isNotNullOrUndefined(_.get(newOrder, 'id')));
    if (newOrder && isChangeOrder) {
        return ContentUtils.replace(orders, newOrder, 'id');
    }
    return orders;
}

function _syncOrderBatchNumberWithModalLayout(orders) {
    const layoutModal = DashboardModalStore.getCurrentLayout();
    const layoutChanged = _.map(layoutModal, position => {
        const positionOrderId = ContentUtils.cleanId(_.get(position, 'i'));
        const ordersWithId = _.filter(orders, order => ContentUtils.isNotNullOrUndefined(_.get(order, 'id')));
        const orderTarget = _.find(ordersWithId, order => _.get(order, 'id') === positionOrderId);
        if (orderTarget && position) {
            position.batchNumber = orderTarget.batchNumber;
        }
        return position;
    });
    DashboardModalStore.setCurrentLayout(layoutChanged);
}

function _retrieveEarlyAndLaterOrder(orders) {
    let earlyDate, laterDate;

    for (const order of orders) {
        const startDate = ContentUtils.getOrderField(order, 'startDate');
        const endDate = ContentUtils.getOrderField(order, 'endDate');
        // initialize target dates
        if (!earlyDate) {
            earlyDate = startDate;
        }

        if (!laterDate) {
            laterDate = endDate;
        }

        if (DateUtils.isBefore(startDate, earlyDate)) {
            earlyDate = startDate;
        }

        if (DateUtils.isAfter(endDate, laterDate)) {
            laterDate = endDate;
        }
    }

    return {
        earlyDate,
        laterDate
    };
}

DashboardModalStore.dispatchToken = SchedulerAppDispatcher.register(function (action) {

    switch (action.type) {

        case ActionTypes.DASHBOARD_MODAL_CHANGE_TOGGLE: {
            DashboardModalStore.setLastToggleConditional(action.lastToggleChanged);
            DashboardModalStore.setToggles(action.isBHAllowed, action.isFVAllowed, action.isBTAllowed);
            DashboardModalStore.emitChange();
            break;
        }

        case ActionTypes.DASHBOARD_MODAL_OPEN_NEW_ORDER: {
            if (DashboardStore.isPublishingDraft()) {
                Notification.show(t('dashboard_notification_please_wait_publication_finished'), 'warning');
                return;
            }
            const order = prepareOrderFromAction(action);

            // update layout, calculate section volumes and update labels to
            DashboardModalStore.updateLayoutAndCalculateSectionVolumeLabels(order);
            break;
        }

        case ActionTypes.DASHBOARD_MODAL_OPEN_EXISTING_ORDER: {
            if (DashboardStore.isPublishingDraft()) {
                Notification.show(t('dashboard_notification_please_wait_publication_finished'), 'warning');
                return;
            }
            const order = prepareOrderFromAction(action);

            // update layout, calculate section volumes and update labels to
            DashboardModalStore.updateLayoutAndCalculateSectionVolumeLabels(order);
            break;
        }

        case ActionTypes.DASHBOARD_MODAL_CHANGE_NUMBER: {
            // update new batch number in context
            const newBatchNumber = _.get(action, 'batchNumber');
            DashboardModalStore.setLastManualBatchNumber(newBatchNumber);  // update last batch number
            DashboardModalStore.setBatchNumber(newBatchNumber);

            const order = prepareOrderFromAction(action);

            // update layout, calculate section volumes and update labels to
            DashboardModalStore.updateLayoutAndCalculateSectionVolumeLabels(order);
            break;
        }

        case ActionTypes.DASHBOARD_MODAL_CHANGE_VOLUME: {
            // update volume in context
            const volume = NumberUtils.standardiseDecimalFormatNumber(_.get(action, 'volume'));
            DashboardModalStore.setLastManualVolume(volume);
            DashboardModalStore.setVolume(volume);

            const order = prepareOrderFromAction(action);

            // update layout, calculate section volumes and update labels to
            DashboardModalStore.updateLayoutAndCalculateSectionVolumeLabels(order);
            break;
        }

        case ActionTypes.DASHBOARD_MODAL_CHANGE_PRODUCT_AMOUNT: {
            // save first the task with the specific product
            DashboardModalStore.setLastManualTask(action.taskUpdated);

            // update product in context
            const order = prepareOrderFromAction(action);

            // update layout, calculate section volumes and update labels to
            DashboardModalStore.updateLayoutAndCalculateSectionVolumeLabels(order);
            break;
        }
        
        case ActionTypes.DASHBOARD_MODAL_CHANGE_TASK_TYPE: {
            // update product in context
            const order = prepareOrderFromAction(action);
    
            // update layout, calculate section volumes and update labels to
            DashboardModalStore.updateLayoutAndCalculateSectionVolumeLabels(order);
            break;
        }

        case ActionTypes.DASHBOARD_MODAL_CHANGE_BEER_STYLE: {
            // After change the beer style it should disable the toggles, in order
            // to avoid future bugs
            DashboardModalEl.disableToggles(() => {
                // clean information related to older beer style products
                const order = prepareOrderFromAction(action);

                // update layout, calculate section volumes and update labels to
                DashboardModalStore.updateLayoutAndCalculateSectionVolumeLabels(order);
            });
            break;
        }

        
        case ActionTypes.LEFT_DOUBLE_CLICK_DASHBOARD_EMPTY: {
            if (DashboardStore.isPublishingDraft()) {
                Notification.show(t('dashboard_notification_please_wait_publication_finished'), 'warning');
                return;
            }

            context._batchNumber = OrderStore.getNextBatchNumber();
            action.preDefinedOrder.batchNumber = context._batchNumber;
            const volume = +_.get(action, 'preDefinedOrder.tankSelected.volume', 0);

            // prepare Modal
            DashboardModalStore.setCurrentLayout(DashboardLayoutStore.getLayout());
            DashboardModalStore.setPristineVolume(volume);
            DashboardModalStore.setVolume(volume);

            // open modal
            DashboardModalEl.openNewOrder(action.preDefinedOrder);

            // close context menu
            ContextMenuEl.close();
            break;
        }

        case ActionTypes.CONTEXT_MENU_CHANGE_TASK_ORDER_DASHBOARD: {
            if (DashboardStore.isPublishingDraft()) {
                Notification.show(t('dashboard_notification_please_wait_publication_finished'), 'warning');
                return;
            }
            DashboardLayoutStore.setAsRelocatedPositions(_.get(action, 'existingOrderInfo.orderSelected.batchNumber'));
            const volume = +_.get(action, 'existingOrderInfo.orderSelected.volume', 0);

            // prepare modal
            DashboardModalStore.setCurrentLayout(getMinimalLayoutFromBatchNumber(context._batchNumber));
            DashboardModalStore.setPristineVolume(volume);
            DashboardModalStore.setVolume(volume);

            // open modal
            const existingOrderInfoWithTotalBatchVolume = BrewhouseStore.calculateBatchVolume(action.existingOrderInfo);
           
            if (_.get(action, 'existingOrderInfo.orderSelected.isSingle')) {
                DashboardModalEl.openExistingSingleOrder(existingOrderInfoWithTotalBatchVolume);
            } else {
                DashboardModalEl.openExistingBrewOrder(existingOrderInfoWithTotalBatchVolume);
            }

            // close context menu
            ContextMenuEl.close();
            break;
        }

        case ActionTypes.CONTEXT_MENU_NEW_ORDER_DASHBOARD: {
            if (DashboardStore.isPublishingDraft()) {
                Notification.show(t('dashboard_notification_please_wait_publication_finished'), 'warning');
                return;
            }

            context._batchNumber = OrderStore.getNextBatchNumber();
            const volume = +_.get(action, 'preDefinedOrder.tankSelected.volume', 0);

            // prepare Modal
            DashboardModalStore.setCurrentLayout(getMinimalLayoutFromBatchNumber(context._batchNumber));
            DashboardModalStore.setPristineVolume(volume);
            DashboardModalStore.setVolume(volume);

            // open modal
            action.preDefinedOrder.batchNumber = context._batchNumber;
            DashboardModalEl.openNewOrder(action.preDefinedOrder);

            // close context menu
            ContextMenuEl.close();
            break;
        }

        case ActionTypes.CONTEXT_MENU_CHANGE_ORDER_DASHBOARD: {
            if (DashboardStore.isPublishingDraft()) {
                Notification.show(t('dashboard_notification_please_wait_publication_finished'), 'warning');
                return;
            }
            DashboardLayoutStore.setAsRelocatedPositions(_.get(action, 'existingOrderInfo.orderSelected.batchNumber'));
            let existingOrderWithTotalBatchVolume = BrewhouseStore.calculateBatchVolume(action.existingOrderInfo);
            const orderSelected = _.get(existingOrderWithTotalBatchVolume, 'orderSelected');
            context._pristineVolume = +_.get(orderSelected, 'volume', 0);
            const batchNumber = _.get(orderSelected, 'batchNumber');
            const volume = +_.get(action, 'existingOrderInfo.orderSelected.volume', 0);

            // prepare Modal
            DashboardModalStore.setPristineVolume(volume);
            DashboardModalStore.setVolume(volume);
            DashboardModalStore.setCurrentLayout(getMinimalLayoutFromBatchNumber(orderSelected.batchNumber));

            // open modal
            const batchVolumeInfo = BrewhouseStore.calculateBatchVolume(existingOrderWithTotalBatchVolume, batchNumber, DashboardModalStore.getCurrentLayout());
            existingOrderWithTotalBatchVolume = _.merge(existingOrderWithTotalBatchVolume, batchVolumeInfo);
         
            if (_.get(action, 'existingOrderInfo.orderSelected.isSingle')) {
                DashboardModalEl.openExistingSingleOrder(existingOrderWithTotalBatchVolume);
            } else {
                DashboardModalEl.openExistingBrewOrder(existingOrderWithTotalBatchVolume);
            }

            // close context menu
            ContextMenuEl.close();
            break;
        }

        case ActionTypes.DASHBOARD_MODAL_CLOSE_MODAL: {
            DashboardModalStore.cleanStore();
            DashboardModalEl.close();
            break;
        }

        case ActionTypes.DASHBOARD_SWITCH_CLOSE_MODAL: {
            DashboardModalStore.cleanStore();
            DashboardModalEl.close();
            break;
        }

        case ActionTypes.LEFT_DOUBLE_CLICK_DASHBOARD_OCCUPIED: {
            if (ContentUtils.isGhostOrder(_.get(action, 'existingOrderInfo.orderSelected'))) {
                // in case that we clicked on order in wrong state, we want to do nothing.
                return;
            }
            let existingOrderInfo = _.get(action, 'existingOrderInfo');
            DashboardLayoutStore.setAsRelocatedPositions(_.get(existingOrderInfo, 'orderSelected.batchNumber'));
            const orderSelected = _.get(existingOrderInfo, 'orderSelected');
            const volume = +_.get(orderSelected, 'volume', 0);
            const batchNumber = _.get(action, 'existingOrderInfo.orderSelected.batchNumber');
            const batchVolumeInfo = BrewhouseStore.calculateBatchVolume(existingOrderInfo, batchNumber, DashboardModalStore.getCurrentLayout());
            const existingOrderInfoUpdated = _.merge(existingOrderInfo, batchVolumeInfo);

            // prepare Modal
            DashboardModalStore.setPristineVolume(volume);
            DashboardModalStore.setVolume(volume);
            DashboardModalStore.setCurrentLayout(getMinimalLayoutFromBatchNumber(orderSelected.batchNumber));
          
            // open modal
            if (_.get(orderSelected, 'isSingle')) {
                DashboardModalEl.openExistingSingleOrder(existingOrderInfoUpdated);
            } else {
                DashboardModalEl.openExistingBrewOrder(existingOrderInfoUpdated);
            }
            break;
        }
        default:
    }

});

function _updateLayoutWithOrder(layout, order) {
    if (order) {
        return _.map(layout, position => {
            const isOrderTarget = ContentUtils.cleanId(position.i) === order.id;
            if (isOrderTarget) {
                position.batchNumber = _.get(order, 'batchNumber');
                position.volume = _.get(order, 'volume');
            }
            return position;
        });
    }
    return layout;
}


/**
 * Standardise the input actions to work in the same way to different scenarios.
 * @param action
 * @return {*}
 */
function prepareOrderFromAction(action) {
    // check actions values first over order values
    let order = _.cloneDeep(_.get(action, 'order') || {}); // clone to prevent unwanted changes in edit mode
    const volume = _.get(action, 'volume') || _.get(order, 'volume');
    const isSingle = _.get(action, 'isSingle') || _.get(order, 'isSingle');

    // prepare virtual/existing order to be handler after properly
    order.isSingle = isSingle;
    order.volume = volume || DashboardModalStore.getLastManualVolume();
    order = _updateBatchNumber(order, action);
    // order = _updateOrderWithLastManualTask(order);

    // get all another orders with the same batch number
    const anotherOrdersWithSameBatchNumber = _.filter(DashboardServiceCache.getOrders(), orderStored => orderStored.batchNumber === order.batchNumber && orderStored.id != order.id);
    const existAnotherOrderWithSameBachNumber = _.get(anotherOrdersWithSameBatchNumber, 'length', 0) > 0;

    if (existAnotherOrderWithSameBachNumber) {
        // put all orders in the same array to process each task from whole batch of orders.
        const allOrdersOfTheBatch = _.concat(anotherOrdersWithSameBatchNumber, _.cloneDeep(order)); // clone to prevent unwanted changes

        // if exist another order with the same batch number we need to identifier which task
        // came from which order. So to do that, we need to add some orderId identifier to each task
        const allOrdersOfTheBatchWhithOrderId = _markEachTaskFromOrderId(allOrdersOfTheBatch);

        // put in the same array all task from all orders with the same batch number
        let allTaskOfTheBatch = [];

        for (const orderFromBatch of allOrdersOfTheBatchWhithOrderId) {
            const tasksFromOrder = _getTaskFromOrderAndSetOrderId(orderFromBatch);
            allTaskOfTheBatch.push(tasksFromOrder);
        }

        // update special list which contain the task with product properly updated
        order.taskWithProductUpdates = _.filter(_.flatten(allTaskOfTheBatch), task => task);

    } else {
        // enter here means the order don't have any order
        // with the same batch number
        const taskFromOrder = _getTaskFromOrderAndSetOrderId(order);

        // update special list which contain the task with product properly updated
        order.taskWithProductUpdates = taskFromOrder;
    }

    // remove undefined positions
    order.taskWithProductUpdates = _.filter(order.taskWithProductUpdates, task => task);
    
    // update total batch cost only should be updated on NEW MODE.
    order = addingBatchCost(order, action);
    
    return order;
}

function addingBatchCost(order, action) {
    if (isNewOrder(action)) {
        order.totalBatchCost = '0';
    } else {
        order.totalBatchCost = BrewhouseStore.calculateCostByOrder(
          order,
          DashboardServiceCache.getVendorItems()
        );
    }
    return order;
}

function isNewOrder(action) {
    return action.type === 'DASHBOARD_MODAL_OPEN_NEW_ORDER' || action.type === 'DASHBOARD_MODAL_CHANGE_BEER_STYLE';
}

function _updateBatchNumber(order, action) {
    const isBatchNumberChanged = _.get(order, 'batchNumber') !== _.get(action, 'batchNumber');

    if (isBatchNumberChanged) {
        order.oldBatchNumber = _.get(order, 'batchNumber');
    }

    order.batchNumber = _.get(action, 'batchNumber') || DashboardModalStore.getLastManualBatchNumber();

    return order;
}

/**
 * Assign the order id identifier to each task of the order.
 * @param orders
 * @return {*}
 * @private
 */
function _markEachTaskFromOrderId(orders) {
    if (orders) {
        for (let order of orders) {
            if (order.isSingle) {
                order.singleTasks = _.map(order.singleTasks || [], task => {
                    task.orderId = order.id;
                    return task;
                });
            } else {
                order.fermenterTasks = _.map(order.fermenterTasks || [], task => {
                    task.orderId = order.id;
                    return task;
                });
                order.packagingTasks = _.map(order.packagingTasks || [], task => {
                    task.orderId = order.id;
                    return task;
                });
            }
            order.batchs = _.map(order.batchs || [], task => {
                task.orderId = order.id;
                return task;
            });
        }
    }

    return orders;
}

function _getTaskFromOrderAndSetOrderId(order) {
    const orderId = _.get(order, 'id');
    if (order.isSingle) {
        const tasks = _.concat(order.singleTasks, order.batchs);
        return _hydratedTasksWithOrderId(tasks, orderId);
    } else {
        const tasks = _.concat(order.batchs, order.fermenterTasks, order.packagingTasks);
        return _hydratedTasksWithOrderId(tasks, orderId);
    }
}

/*function _updateOrderWithLastManualTask(order) {
    const lastManualTasks = DashboardModalStore.getLastManualTask();
    if (order && ContentUtils.isNotEmptyArray(lastManualTasks)) {
        for (const manualTask of lastManualTasks) {
            let taskFieldName;

            const conf = ContentUtils.getTankTypeConfiguration(_.get(manualTask, 'sectionType'));
            if (conf) {
                taskFieldName = _.get(conf, 'tasksFieldName');
            }

            if (taskFieldName) {
                const existingTask = order[taskFieldName];
                let removeManualTask = _removeDefaultTask(existingTask, manualTask) || [];
                removeManualTask.push(manualTask);
                order[taskFieldName] = removeManualTask;
            }
        }
    }
    return order;
}*/

/*
function _removeDefaultTask(tasks, manualTasks) {
    if (!tasks || !manualTasks) {
        return tasks;
    }

    if (manualTasks.turn) {
        // is batch tasks
        return tasks.filter(task => String(task.turnId) !== String(manualTasks.turnId));
    }
    
    if (!Array.isArray(manualTasks)) {
        manualTasks = [manualTasks];
    }
    
    return _.filter(tasks, task => {
        return !_.some(manualTasks, manualTask =>
            (
                _.get(task, 'date') === _.get(manualTask, 'date') &&
                _.get(task, 'sectionType') === _.get(manualTask, 'sectionType'))
        );
    });
}
*/

function _hydratedTasksWithOrderId(tasks, orderId) {
    if (tasks && orderId) {
        return _.map(tasks, task => {
            if (task) {
                task.orderId = orderId;
            }
            return task;
        });
    }
    return tasks;
}


module.exports = DashboardModalStore;



/** WEBPACK FOOTER **
 ** ./src/stores/DashboardModalStore.js
 **/